/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */
import React, { useCallback, useEffect, useState } from "react";

import { BROKER_ACTIVACION } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import regionLogo from "../../img/region-logo.png";
import { getContratos, getOportunidades, getUsers, getClientes, getEdificios } from "../../utils";
import {
  calcularPublicidadMensual,
  calcularBTLMensual,
  calcularHardwareMensual,
  calcularGestionContenidosMensual,
  normalizeMonth,
  convertirAMonedaLocal,
} from "../../utils/calculateVentas";
import { getClientesPorBroker } from "../../utils/cotizacionesGuardadas";
import { loadExchangeRates } from "../../utils/exchangeRateService";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";

import { ChartModal } from "./components/ChartModal";
import { DetailsModal } from "./components/DetailsModal";
import { PipelineModal } from "./components/PipelineModal";
import { VentasTable } from "./components/VentasTable";
import { VPHeader } from "./components/VPHeader";
import { FACTOR_90, FACTOR_75 } from "./constants";
import { useVPAggregation } from "./hooks/useVPAggregation";
import { useVPCierresCalcs } from "./hooks/useVPCierresCalcs";
import { useVPFacturacionCalcs } from "./hooks/useVPFacturacionCalcs";
import { useVPFormatting } from "./hooks/useVPFormatting";
import { useVPPipelineCalcs } from "./hooks/useVPPipelineCalcs";
import styles from "./components/stylesVentasTabla.module.scss";

import "./stylesVentasProyeccion.scss";

const VentasProyeccion = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  // UI State
  // eslint-disable-next-line no-unused-vars
  const [filtroAno, setFiltroAno] = useState("2025");
  const [showInDollars, setShowInDollars] = useState(false);
  const [mostrar2025, setMostrar2025] = useState(false);
  const [vistaCompleta, setVistaCompleta] = useState("cierres");
  const [verChileYPeru, setVerChileYPeru] = useState(false);
  const [verYTD, setVerYTD] = useState(false);

  // Data State - país actual
  const [oportunidades, setOportunidades] = useState([]);
  const [salesBTL, setSalesBTL] = useState([]);
  const [negociosCarteleria, setNegociosCarteleria] = useState([]);
  const [costosGestionData, setCostosGestionData] = useState([]);
  const [contratosData, setContratosData] = useState([]);

  // Data State - otro país (modo combinado)
  const [oportunidadesOtroPais, setOportunidadesOtroPais] = useState([]);
  const [salesBTLOtroPais, setSalesBTLOtroPais] = useState([]);
  const [negociosCarteleriaOtroPais, setNegociosCarteleriaOtroPais] = useState([]);
  const [costosGestionDataOtroPais, setCostosGestionDataOtroPais] = useState([]);
  const [contratosDataOtroPais, setContratosDataOtroPais] = useState([]);
  const [datosOtroPaisCargados, setDatosOtroPaisCargados] = useState(false);

  // Modal State
  const [modal, setModal] = useState({ open: false, title: "", items: [] });
  const [pipelineModal, setPipelineModal] = useState({ open: false, title: "", items: [], total: 0 });
  const [pipelineSort, setPipelineSort] = useState("importe");
  const [chartModalOpen, setChartModalOpen] = useState(false);

  // Lookup data
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState({});
  const [edificios, setEdificios] = useState({});
  const [loading, setLoading] = useState(true);

  // Calculated State - país actual
  const [cierres2025, setCierres2025] = useState(null);
  const [cierres2026, setCierres2026] = useState(null);
  const [facturacion2024, setFacturacion2024] = useState(null);
  const [facturacion2025, setFacturacion2025] = useState(null);
  const [facturacion2026, setFacturacion2026] = useState(null);

  // Calculated State - otro país
  const [cierres2025OtroPais, setCierres2025OtroPais] = useState(null);
  const [cierres2026OtroPais, setCierres2026OtroPais] = useState(null);
  const [facturacion2024OtroPais, setFacturacion2024OtroPais] = useState(null);
  const [facturacion2025OtroPais, setFacturacion2025OtroPais] = useState(null);
  const [facturacion2026OtroPais, setFacturacion2026OtroPais] = useState(null);

  const { pais, rol, agencia } = useAuthContext() || {};
  const modoCombinado = verChileYPeru && datosOtroPaisCargados;

  // === HOOKS ===

  const { dolarMensual, dolarMensualOtroPais, tipoCambioActual, dolarFallback } = useExchangeRates(pais);
  const otroPaisNombre = pais === "Chile" ? "Perú" : "Chile";
  const tipoCambioOtroPais = dolarFallback[otroPaisNombre] || 0;

  const { localString, moneda, getMoneda, getDisplayValue, getMonthName, calcularVariacion, shouldShowVsAA } =
    useVPFormatting(pais, showInDollars, modoCombinado);

  const {
    calcularCierresPub,
    calcularCierresBTL,
    calcularCierresHW,
    calcularCierresCart,
    calcularCierresContratos,
    calcularCierresPubOtro,
    calcularCierresBTLOtro,
    calcularCierresHWOtro,
    calcularCierresCartOtro,
    calcularCierresContratosOtro,
  } = useVPCierresCalcs({
    oportunidades,
    salesBTL,
    negociosCarteleria,
    contratosData,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const {
    calcularFacturacionPubProyeccion,
    calcularFacturacionBTLProyeccion,
    calcularFacturacionContratosProyeccion,
    calcularFacturacionPubProyeccionOtro,
    calcularFacturacionBTLProyeccionOtro,
    calcularFacturacionContratosProyeccionOtro,
  } = useVPFacturacionCalcs({
    oportunidades,
    salesBTL,
    contratosData,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    contratosDataOtroPais,
    pais,
  });

  const { getDesglose } = useVPAggregation({
    cierres2025,
    cierres2026,
    facturacion2024,
    facturacion2025,
    facturacion2026,
    cierres2025OtroPais,
    cierres2026OtroPais,
    facturacion2024OtroPais,
    facturacion2025OtroPais,
    facturacion2026OtroPais,
    showInDollars,
    modoCombinado,
    dolarMensual,
    dolarMensualOtroPais,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
  });

  const {
    calcularPipeline,
    calcularPipelineBruto,
    calcularPipelineBrutoTotal,
    calcularPipelineFacturacion,
    getPipelineItems,
  } = useVPPipelineCalcs({
    oportunidades,
    oportunidadesOtroPais,
    contratosData,
    contratosOtroPais: contratosDataOtroPais,
    showInDollars,
    modoCombinado,
    tipoCambioActual,
    tipoCambioOtroPais,
    pais,
  });

  // === DATA LOADING ===

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Garantiza que tipos de cambio (UF/USD) esten cargados antes de cualquier
        // calculo que convierta monedas. Evita race condition donde los calculos
        // corren con cache vacio y convierten USD→CLP como 0.
        await loadExchangeRates();
        const proyResults = await Promise.all([
          getOportunidades({ pais }),
          getUsers(),
          client.from("negocios_carteleria").select("*").eq("pais", pais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", pais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", pais),
          getClientes({ pais }),
          getEdificios({ pais }),
          getContratos({ pais }),
        ]);
        const usuariosData = proyResults[1];
        const negociosData = proyResults[2];
        const costosGestionRaw = proyResults[4];
        const edificiosData = proyResults[6];
        // let: se reasignan al scopear por cartera cuando rol===BROKER
        let allOportunidades = proyResults[0];
        let clientesData = proyResults[5];
        let salesBTLData = proyResults[3];
        let contratosRaw = proyResults[7];

        // === SCOPING BROKER (app-level, fail-safe restrictivo) ===
        // El broker solo ve las VENTAS de su cartera (broker_id = su agencia). Costos país-level
        // (cartelería/gestión) quedan sin filtrar (no tienen client_id). Fail-safe: si no resuelve
        // cartera → vacío, nunca sin filtrar. Demás roles: sin cambios.
        if (rol === ROLES.BROKER) {
          let carteraIds = new Set();
          try {
            const cartera = await getClientesPorBroker(pais, agencia);
            carteraIds = new Set((cartera || []).map((c) => Number(c.id)));
          } catch {
            carteraIds = new Set();
          }
          const enCartera = (cid) => carteraIds.has(Number(cid));
          allOportunidades = (allOportunidades || []).filter((o) => enCartera(o.client_id));
          clientesData = (clientesData || []).filter((c) => carteraIds.has(Number(c.id)));
          salesBTLData = { ...salesBTLData, data: (salesBTLData?.data || []).filter((b) => enCartera(b.client_id)) };
          contratosRaw = (contratosRaw || []).filter((c) => enCartera(c.client_id));

          // Filtro temporal broker: pipeline abierto (fecha_cierre null) o cerrado desde
          // BROKER_ACTIVACION en adelante. Ventas cerradas antes se ocultan.
          const brokerDesde = (o) => !o.fecha_cierre || o.fecha_cierre >= BROKER_ACTIVACION;
          allOportunidades = allOportunidades.filter(brokerDesde);
          contratosRaw = contratosRaw.filter(brokerDesde);
          salesBTLData = { ...salesBTLData, data: (salesBTLData?.data || []).filter(brokerDesde) };
        }

        setUsuarios(usuariosData || []);
        setNegociosCarteleria(negociosData.data || []);
        setCostosGestionData(costosGestionRaw.data || []);

        const clientesMap = {};
        (clientesData || []).forEach((c) => {
          clientesMap[c.id] = c;
        });
        setClientes(clientesMap);

        const edificiosMap = {};
        (edificiosData || []).forEach((e) => {
          edificiosMap[e.id] = e;
        });
        setEdificios(edificiosMap);

        const filteredOportunidades = allOportunidades
          .filter((o) => o.pais === pais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidades(filteredOportunidades);

        const filteredBTL = (salesBTLData.data || [])
          .filter((btl) => btl.pais === pais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTL(filteredBTL);

        const filteredContratos = (contratosRaw || []).filter((c) => c.pais === pais && !c.eliminada);
        setContratosData(filteredContratos);
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pais, rol, agencia]);

  // Cargar datos del OTRO país cuando se activa modo combinado
  useEffect(() => {
    if (!verChileYPeru || datosOtroPaisCargados) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const fetchDatosOtroPais = async () => {
      try {
        const otroResults = await Promise.all([
          getOportunidades({ pais: otroPais }),
          client.from("negocios_carteleria").select("*").eq("pais", otroPais).neq("eliminada", true),
          client.from("sales_btl").select("*").eq("pais", otroPais).eq("eliminada", false),
          client.from("costos_gestion_contenidos").select("*").eq("pais", otroPais),
          getContratos({ pais: otroPais }),
        ]);
        const negociosOtro = otroResults[1];
        const costosGestionOtro = otroResults[3];
        let oportunidadesOtro = otroResults[0];
        let salesBTLOtro = otroResults[2];
        let contratosOtro = otroResults[4];

        // === SCOPING BROKER en modo combinado (otro país) — mismo criterio, sin leak ===
        if (rol === ROLES.BROKER) {
          let carteraOtroIds = new Set();
          try {
            const carteraOtro = await getClientesPorBroker(otroPais, agencia);
            carteraOtroIds = new Set((carteraOtro || []).map((c) => Number(c.id)));
          } catch {
            carteraOtroIds = new Set();
          }
          const enCarteraOtro = (cid) => carteraOtroIds.has(Number(cid));
          oportunidadesOtro = (oportunidadesOtro || []).filter((o) => enCarteraOtro(o.client_id));
          salesBTLOtro = {
            ...salesBTLOtro,
            data: (salesBTLOtro?.data || []).filter((b) => enCarteraOtro(b.client_id)),
          };
          contratosOtro = (contratosOtro || []).filter((c) => enCarteraOtro(c.client_id));

          // Filtro temporal broker (otro país): mismo criterio que el bloque principal.
          const brokerDesdeOtro = (o) => !o.fecha_cierre || o.fecha_cierre >= BROKER_ACTIVACION;
          oportunidadesOtro = oportunidadesOtro.filter(brokerDesdeOtro);
          contratosOtro = contratosOtro.filter(brokerDesdeOtro);
          salesBTLOtro = { ...salesBTLOtro, data: (salesBTLOtro?.data || []).filter(brokerDesdeOtro) };
        }

        const filteredOportunidadesOtro = oportunidadesOtro
          .filter((o) => o.pais === otroPais && !o.eliminada)
          .map((o) => ({
            ...o,
            mes_cierre: normalizeMonth(o.mes_cierre, o.created_at, o.ano),
            mes_factura: normalizeMonth(o.mes_factura, o.created_at, o.ano),
          }));
        setOportunidadesOtroPais(filteredOportunidadesOtro);

        setNegociosCarteleriaOtroPais(negociosOtro.data || []);
        setCostosGestionDataOtroPais(costosGestionOtro.data || []);

        const filteredBTLOtro = (salesBTLOtro.data || [])
          .filter((btl) => btl.pais === otroPais && !btl.eliminada)
          .map((btl) => ({
            ...btl,
            mes_cierre: normalizeMonth(btl.mes_cierre, btl.created_at, btl.ano),
            mes_factura: normalizeMonth(btl.mes_factura, btl.created_at, btl.ano),
          }));
        setSalesBTLOtroPais(filteredBTLOtro);

        const filteredContratosOtro = (contratosOtro || []).filter((c) => c.pais === otroPais && !c.eliminada);
        setContratosDataOtroPais(filteredContratosOtro);

        setDatosOtroPaisCargados(true);
      } catch (error) {
        console.error("Error cargando datos del otro país:", error);
      }
    };

    fetchDatosOtroPais();
  }, [verChileYPeru, datosOtroPaisCargados, pais, rol, agencia]);

  // === CALCULATION ORCHESTRATION ===

  useEffect(() => {
    if (loading) return;

    const calcularTodo = async () => {
      try {
        // Re-asegura cache de tipos de cambio antes de calcular
        // (defensivo: si fetchData termino pero el cache se invalido).
        await loadExchangeRates();
        // Helper: suma element-wise de dos arrays de 12
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));

        // CIERRES (contratos sumados en publicidad)
        const pub2025C = sumarArrays(calcularCierresPub(2025), calcularCierresContratos(2025));
        const btl2025C = calcularCierresBTL(2025);
        const hw2025C = calcularCierresHW(2025);
        const cart2025C = calcularCierresCart(2025);
        setCierres2025({ publicidad: pub2025C, hardware: hw2025C, carteleria: cart2025C, btl: btl2025C });

        const pub2026C = sumarArrays(calcularCierresPub(2026), calcularCierresContratos(2026));
        const btl2026C = calcularCierresBTL(2026);
        const hw2026C = calcularCierresHW(2026);
        const cart2026C = calcularCierresCart(2026);
        setCierres2026({ publicidad: pub2026C, hardware: hw2026C, carteleria: cart2026C, btl: btl2026C });

        // FACTURACIÓN 2024 (comparativo AAA - solo facturafecha)
        const pub2024F = await calcularPublicidadMensual(2024, oportunidades, pais);
        const cont2024F = calcularFacturacionContratosProyeccion(2024);
        const btl2024F = await calcularBTLMensual(2024, salesBTL, pais);
        const hw2024F = await calcularHardwareMensual(2024, negociosCarteleria, pais, costosGestionData);
        const cart2024F = await calcularGestionContenidosMensual(2024, negociosCarteleria, pais, costosGestionData);
        setFacturacion2024({
          publicidad: sumarArrays(pub2024F, cont2024F),
          hardware: hw2024F,
          carteleria: cart2024F,
          btl: btl2024F,
        });

        // FACTURACIÓN CON PROYECCIÓN (facturafecha + mes_factura)
        const pub2025F = sumarArrays(
          calcularFacturacionPubProyeccion(2025),
          calcularFacturacionContratosProyeccion(2025)
        );
        const btl2025F = calcularFacturacionBTLProyeccion(2025);
        const hw2025F = await calcularHardwareMensual(2025, negociosCarteleria, pais, costosGestionData);
        const cart2025F = await calcularGestionContenidosMensual(2025, negociosCarteleria, pais, costosGestionData);
        setFacturacion2025({ publicidad: pub2025F, hardware: hw2025F, carteleria: cart2025F, btl: btl2025F });

        const pub2026F = sumarArrays(
          calcularFacturacionPubProyeccion(2026),
          calcularFacturacionContratosProyeccion(2026)
        );
        const btl2026F = calcularFacturacionBTLProyeccion(2026);
        const hw2026F = await calcularHardwareMensual(2026, negociosCarteleria, pais, costosGestionData);
        const cart2026F = await calcularGestionContenidosMensual(2026, negociosCarteleria, pais, costosGestionData);
        setFacturacion2026({ publicidad: pub2026F, hardware: hw2026F, carteleria: cart2026F, btl: btl2026F });
      } catch (error) {
        console.error("Error calculando:", error);
      }
    };

    calcularTodo();
  }, [loading, oportunidades, salesBTL, negociosCarteleria, costosGestionData, contratosData, pais]);

  // CÁLCULOS OTRO PAÍS (modo combinado)
  useEffect(() => {
    if (!modoCombinado || oportunidadesOtroPais.length === 0) return;

    const otroPais = pais === "Chile" ? "Perú" : "Chile";

    const calcularTodoOtroPais = async () => {
      try {
        const sumarArrays = (a, b) => a.map((v, i) => v + (b[i] || 0));

        const pub2025C = sumarArrays(calcularCierresPubOtro(2025), calcularCierresContratosOtro(2025));
        const btl2025C = calcularCierresBTLOtro(2025);
        const hw2025C = calcularCierresHWOtro(2025);
        const cart2025C = calcularCierresCartOtro(2025);
        setCierres2025OtroPais({ publicidad: pub2025C, hardware: hw2025C, carteleria: cart2025C, btl: btl2025C });

        const pub2026C = sumarArrays(calcularCierresPubOtro(2026), calcularCierresContratosOtro(2026));
        const btl2026C = calcularCierresBTLOtro(2026);
        const hw2026C = calcularCierresHWOtro(2026);
        const cart2026C = calcularCierresCartOtro(2026);
        setCierres2026OtroPais({ publicidad: pub2026C, hardware: hw2026C, carteleria: cart2026C, btl: btl2026C });

        const negocios = negociosCarteleriaOtroPais;
        const costos = costosGestionDataOtroPais;

        const pub2024F = await calcularPublicidadMensual(2024, oportunidadesOtroPais, otroPais);
        const cont2024F = calcularFacturacionContratosProyeccionOtro(2024);
        const btl2024F = await calcularBTLMensual(2024, salesBTLOtroPais, otroPais);
        const hw2024F = await calcularHardwareMensual(2024, negocios, otroPais, costos);
        const cart2024F = await calcularGestionContenidosMensual(2024, negocios, otroPais, costos);
        setFacturacion2024OtroPais({
          publicidad: sumarArrays(pub2024F, cont2024F),
          hardware: hw2024F,
          carteleria: cart2024F,
          btl: btl2024F,
        });

        const pub2025F = sumarArrays(
          calcularFacturacionPubProyeccionOtro(2025),
          calcularFacturacionContratosProyeccionOtro(2025)
        );
        const btl2025F = calcularFacturacionBTLProyeccionOtro(2025);
        const hw2025F = await calcularHardwareMensual(2025, negocios, otroPais, costos);
        const cart2025F = await calcularGestionContenidosMensual(2025, negocios, otroPais, costos);
        setFacturacion2025OtroPais({
          publicidad: pub2025F,
          hardware: hw2025F,
          carteleria: cart2025F,
          btl: btl2025F,
        });

        const pub2026F = sumarArrays(
          calcularFacturacionPubProyeccionOtro(2026),
          calcularFacturacionContratosProyeccionOtro(2026)
        );
        const btl2026F = calcularFacturacionBTLProyeccionOtro(2026);
        const hw2026F = await calcularHardwareMensual(2026, negocios, otroPais, costos);
        const cart2026F = await calcularGestionContenidosMensual(2026, negocios, otroPais, costos);
        setFacturacion2026OtroPais({
          publicidad: pub2026F,
          hardware: hw2026F,
          carteleria: cart2026F,
          btl: btl2026F,
        });
      } catch (error) {
        console.error("Error calculando otro país:", error);
      }
    };

    calcularTodoOtroPais();
  }, [
    modoCombinado,
    oportunidadesOtroPais,
    salesBTLOtroPais,
    negociosCarteleriaOtroPais,
    costosGestionDataOtroPais,
    contratosDataOtroPais,
    pais,
  ]);

  // === MODAL HANDLERS ===

  const handleOpenModal = useCallback(
    (monthIndex, tipo, year, vertical) => {
      const ano = Number(year || currentYear);
      const start = monthIndex + 1;
      const end = monthIndex + 1;
      const isCierres = tipo === "cierres";
      const campoMes = isCierres ? "mes_cierre" : "mes_factura";

      let todas = [];

      if (vertical === "publicidad" || vertical === null) {
        todas = [
          ...todas,
          ...oportunidades
            .filter((o) => {
              if (o.estatus !== "100%" || o.eliminada) return false;
              // Misma lógica de prioridad que useVPFacturacionCalcs:
              // facturafecha (real) > mes_factura (proyección) para facturación
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && o[campoMes]) {
                const [mesStr, anoStr] = o[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((o) => {
              // Ejecutivo = dueño de la cuenta (cliente.ejecutivo), fallback al de la venta
              const ejecutivoId = clientes[o.client_id]?.ejecutivo ?? o.ejecutivo;
              const usuarioObj = usuarios.find((u) => String(u.id) === String(ejecutivoId));
              const importeRaw = parseFloat(o.importe) || 0;
              // Resolver mes con misma prioridad
              let mesIdx = -1;
              if (!isCierres && o.facturafecha) {
                const d = new Date(o.facturafecha);
                if (!isNaN(d.getTime())) mesIdx = d.getMonth();
              }
              if (mesIdx < 0 && o[campoMes]) {
                mesIdx = Number(o[campoMes].split("-")[0]) - 1;
              }
              const ultimoDia = new Date(ano, (mesIdx >= 0 ? mesIdx : 0) + 1, 0);
              const fechaConv = ultimoDia.toISOString().split("T")[0];
              const importeLocal = o.moneda_dolar
                ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConv)
                : importeRaw;
              return {
                ...o,
                esBTL: false,
                esCarteleria: false,
                ejecutivo: usuarioObj ? usuarioObj.nombre : "Sin asignar",
                cliente: o.cliente || "Sin cliente",
                importe: importeLocal,
                importeOriginalMoneda: importeRaw,
              };
            }),
        ];

        // CONTRATOS (sumados en publicidad)
        if (isCierres) {
          todas = [
            ...todas,
            ...contratosData
              .filter((c) => {
                if (c.estatus !== "100%" || c.eliminada || !c.mes_cierre) return false;
                const [mesStr, anoStr] = c.mes_cierre.split("-");
                return Number(anoStr) === ano && Number(mesStr) >= start && Number(mesStr) <= end;
              })
              .map((c) => {
                const clienteObj = clientes[c.client_id];
                const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
                const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
                const usuarioObj = usuarios.find((u) => String(u.id) === String(ejecutivoId));
                const importeRaw = Number(c.importe_total) || 0;
                const mesIdxC = Number(c.mes_cierre?.split("-")[0]) - 1;
                const ultimoDiaC = new Date(ano, (mesIdxC >= 0 ? mesIdxC : 0) + 1, 0);
                const fechaConvC = ultimoDiaC.toISOString().split("T")[0];
                const importeLocal = c.moneda_dolar
                  ? convertirAMonedaLocal(importeRaw, "USD", pais, fechaConvC)
                  : importeRaw;
                return {
                  ...c,
                  esBTL: false,
                  esCarteleria: false,
                  esHardware: false,
                  esContrato: true,
                  cliente: clienteNombre,
                  ejecutivo: usuarioObj ? usuarioObj.nombre : c.display_id || "Sin asignar",
                  importe: importeLocal,
                  importeOriginalMoneda: importeRaw,
                };
              }),
          ];
        } else {
          // Facturación: explotar JSONB meses
          contratosData
            .filter((c) => !c.eliminada && c.estatus === "100%")
            .forEach((c) => {
              const clienteObj = clientes[c.client_id];
              const clienteNombre = clienteObj?.empresa || clienteObj?.nombre || c.cliente || "Sin cliente";
              const ejecutivoId = clienteObj?.ejecutivo ?? c.ejecutivo;
              const usuarioObj = usuarios.find((u) => String(u.id) === String(ejecutivoId));
              (c.meses || []).forEach((mesObj) => {
                let monthIdx = -1;
                let yearVal = -1;
                if (mesObj.facturafecha) {
                  const d = new Date(mesObj.facturafecha);
                  if (!isNaN(d.getTime())) {
                    monthIdx = d.getMonth() + 1;
                    yearVal = d.getFullYear();
                  }
                } else if (mesObj.mes) {
                  const [m, y] = mesObj.mes.split("-");
                  monthIdx = Number(m);
                  yearVal = Number(y);
                }
                if (yearVal === ano && monthIdx >= start && monthIdx <= end) {
                  const importeRawC = Number(mesObj.importe) || 0;
                  const ultimoDiaFact = new Date(ano, monthIdx, 0);
                  const fechaConvFact = ultimoDiaFact.toISOString().split("T")[0];
                  const importeLocalC = c.moneda_dolar
                    ? convertirAMonedaLocal(importeRawC, "USD", pais, fechaConvFact)
                    : importeRawC;
                  todas.push({
                    ...c,
                    id: `${c.id}-${mesObj.mes}`,
                    esBTL: false,
                    esCarteleria: false,
                    esHardware: false,
                    esContrato: true,
                    cliente: clienteNombre,
                    ejecutivo: usuarioObj ? usuarioObj.nombre : c.display_id || "Sin asignar",
                    importe: importeLocalC,
                    importeOriginalMoneda: importeRawC,
                    mes_factura: mesObj.mes,
                  });
                }
              });
            });
        }
      }

      if (vertical === "btl" || vertical === null) {
        todas = [
          ...todas,
          ...salesBTL
            .filter((b) => {
              if (b.status !== 100 || b.eliminada) return false;
              let mesNum = -1;
              let anoNum = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) {
                  mesNum = d.getMonth() + 1;
                  anoNum = d.getFullYear();
                }
              }
              if (mesNum < 0 && b[campoMes]) {
                const [mesStr, anoStr] = b[campoMes].split("-");
                mesNum = Number(mesStr);
                anoNum = Number(anoStr);
              }
              return anoNum === ano && mesNum >= start && mesNum <= end;
            })
            .map((b) => {
              const invRaw = Number(b.total_investment) || 0;
              let mesIdxBTL = -1;
              if (!isCierres && b.facturafecha) {
                const d = new Date(b.facturafecha);
                if (!isNaN(d.getTime())) mesIdxBTL = d.getMonth();
              }
              if (mesIdxBTL < 0 && b[campoMes]) {
                mesIdxBTL = Number(b[campoMes].split("-")[0]) - 1;
              }
              const ultimoDiaBTL = new Date(ano, (mesIdxBTL >= 0 ? mesIdxBTL : 0) + 1, 0);
              const fechaConvBTL = ultimoDiaBTL.toISOString().split("T")[0];
              const invLocal = b.moneda_dolar ? convertirAMonedaLocal(invRaw, "USD", pais, fechaConvBTL) : invRaw;
              return {
                ...b,
                esBTL: true,
                esCarteleria: false,
                esHardware: false,
                cliente: b.cliente || "Sin cliente",
                total_investment: invLocal,
                importeOriginalMoneda: invRaw,
              };
            }),
        ];
      }

      // HARDWARE
      if (vertical === "hardware" || vertical === null) {
        const hardwareItems = negociosCarteleria
          .filter((n) => {
            if (!n.incluye_hardware || n.eliminada) return false;
            if (!["100%", "Activo"].includes(n.estado)) return false;
            return true;
          })
          .map((n) => {
            // Cascada: edificios → clientes → fallback (igual que CarteleriaList)
            let nombreReferencia = `ID ${n.referencia_id || n.id}`;
            if (n.referencia_id && edificios[n.referencia_id]) {
              nombreReferencia = edificios[n.referencia_id]?.edificio;
            } else if (n.referencia_id && clientes[n.referencia_id]) {
              nombreReferencia = clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
            }

            const monedaHW = (n.moneda_hardware || "UF").toUpperCase();
            const esUF = monedaHW.includes("UF");

            let importeMesOriginal = 0;
            let importeMesConvertido = 0;
            let incluyeEnMes = false;
            const ultimoDiaMes = new Date(ano, end, 0);
            const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

            if (isCierres) {
              const fecha = new Date(n.fecha_cierre);
              if (!isNaN(fecha)) {
                const mesNegocio = fecha.getMonth() + 1;
                const anoNegocio = fecha.getFullYear();
                if (anoNegocio === ano && mesNegocio >= start && mesNegocio <= end) {
                  incluyeEnMes = true;
                  importeMesOriginal = Number(n.monto_hardware) || 0;
                  importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                }
              }
            } else {
              const tieneDesglose =
                n.modalidad === "CAPEX" &&
                n.detalle_pagos_hardware &&
                Array.isArray(n.detalle_pagos_hardware) &&
                n.detalle_pagos_hardware.some((p) => p.fecha_factura && p.monto != null);

              if (tieneDesglose) {
                const pagosMes = n.detalle_pagos_hardware.filter((pago) => {
                  if (!pago.fecha_factura) return false;
                  const fecha = new Date(pago.fecha_factura);
                  const mes = fecha.getMonth() + 1;
                  const añoPago = fecha.getFullYear();
                  return añoPago === ano && mes >= start && mes <= end;
                });
                if (pagosMes.length > 0) {
                  incluyeEnMes = true;
                  importeMesOriginal = pagosMes.reduce((sum, pago) => sum + (Number(pago.monto) || 0), 0);
                  importeMesConvertido = pagosMes.reduce((sum, pago) => {
                    const fechaPago = pago.fecha_factura?.split("T")[0] || fechaConversion;
                    return sum + convertirAMonedaLocal(Number(pago.monto) || 0, monedaHW, pais, fechaPago);
                  }, 0);
                }
              } else if (n.modalidad === "CAPEX" && n.fecha_cierre) {
                const fecha = new Date(n.fecha_cierre);
                const mes = fecha.getMonth() + 1;
                const añoFecha = fecha.getFullYear();
                if (añoFecha === ano && mes >= start && mes <= end) {
                  incluyeEnMes = true;
                  importeMesOriginal = Number(n.monto_hardware) || 0;
                  importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                }
              } else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
                const fechaCierre = new Date(n.fecha_cierre);
                const mesCierre = fechaCierre.getMonth();
                const añoCierre = fechaCierre.getFullYear();
                const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;

                for (let i = 0; i < totalCuotas; i++) {
                  const mesTotal = mesCierre + i;
                  const añoActual = añoCierre + Math.floor(mesTotal / 12);
                  const mesActual = mesTotal % 12;
                  if (añoActual === ano && mesActual + 1 >= start && mesActual + 1 <= end) {
                    incluyeEnMes = true;
                    const pantallas = Number(n.cantidad_pantallas) || 1;
                    importeMesOriginal = Number(n.cuota_hardware_opex) * pantallas;
                    importeMesConvertido = convertirAMonedaLocal(importeMesOriginal, monedaHW, pais, fechaConversion);
                    break;
                  }
                }
              }
            }

            return {
              ...n,
              esBTL: false,
              esCarteleria: false,
              esHardware: true,
              cliente: nombreReferencia,
              importe: importeMesConvertido,
              importeOriginal: importeMesOriginal,
              monedaOriginal: monedaHW,
              esMonedaUF: esUF,
              incluyeEnMes,
            };
          })
          .filter((item) => item.incluyeEnMes);

        todas = [...todas, ...hardwareItems];
      }

      // CARTELERÍA
      if (vertical === "carteleria" || vertical === null) {
        const resolverNombreReferencia = (n) => {
          if (n.referencia_id && edificios[n.referencia_id]) {
            return edificios[n.referencia_id]?.edificio;
          }
          if (n.referencia_id && clientes[n.referencia_id]) {
            return clientes[n.referencia_id]?.empresa || clientes[n.referencia_id]?.nombre;
          }
          return `ID ${n.referencia_id || n.id}`;
        };

        if (isCierres) {
          const ultimoDiaMes = new Date(ano, end, 0);
          const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

          negociosCarteleria.forEach((n) => {
            if (n.eliminada) return;
            if (!["100%", "Activo"].includes(n.estado)) return;

            const pantallas = parseInt(n.cantidad_pantallas) || 1;
            const plazo = parseInt(n.plazo_contrato_meses) || 0;
            const gestion = parseFloat(n.fee_gestion_unitario) || 0;
            const contenidosUnit = parseFloat(n.fee_contenidos_unitario) || 0;
            const tieneContenidosRangoPropio = Boolean(n.incluye_contenidos && n.contenidos_fecha_inicio);
            const contenidosEnBase = n.incluye_contenidos && !tieneContenidosRangoPropio ? contenidosUnit : 0;

            const monedaGestion = (n.moneda_gestion || "UF").toUpperCase();
            const monedaContenidos = (n.moneda_contenidos || "UF").toUpperCase();
            const esUF = monedaGestion.includes("UF") || monedaContenidos.includes("UF");
            const nombreReferencia = resolverNombreReferencia(n);

            // Cierre base (gestión + contenidos si no hay rango propio)
            if (n.fecha_cierre) {
              const fecha = new Date(n.fecha_cierre + "T00:00:00");
              if (!isNaN(fecha)) {
                const mesNegocio = fecha.getMonth() + 1;
                if (fecha.getFullYear() === ano && mesNegocio >= start && mesNegocio <= end) {
                  const gestionLocal = convertirAMonedaLocal(gestion * pantallas, monedaGestion, pais, fechaConversion);
                  const contenidosLocal = convertirAMonedaLocal(
                    contenidosEnBase * pantallas,
                    monedaContenidos,
                    pais,
                    fechaConversion
                  );
                  const montoMensualOriginal = (gestion + contenidosEnBase) * pantallas;
                  const montoTotalOriginal = montoMensualOriginal * plazo;
                  const montoTotalCLP = (gestionLocal + contenidosLocal) * plazo;

                  todas.push({
                    ...n,
                    esBTL: false,
                    esCarteleria: true,
                    esHardware: false,
                    cliente: nombreReferencia,
                    importe: montoTotalCLP,
                    importeOriginal: montoTotalOriginal,
                    monedaOriginal: monedaGestion,
                    esMonedaUF: esUF,
                    pantallas,
                    plazo,
                    feeGestion: gestion,
                    feeContenidos: contenidosEnBase,
                  });
                }
              }
            }

            // Cierre adicional de contenidos con rango propio
            if (tieneContenidosRangoPropio) {
              const fechaCont = new Date(n.contenidos_fecha_inicio + "T00:00:00");
              if (!isNaN(fechaCont)) {
                const mesCont = fechaCont.getMonth() + 1;
                if (fechaCont.getFullYear() === ano && mesCont >= start && mesCont <= end) {
                  const fechaFinCont = n.contenidos_fecha_fin ? new Date(n.contenidos_fecha_fin + "T00:00:00") : null;
                  const plazoCont = fechaFinCont
                    ? (fechaFinCont.getFullYear() - fechaCont.getFullYear()) * 12 +
                      (fechaFinCont.getMonth() - fechaCont.getMonth()) +
                      1
                    : parseInt(n.plazo_contrato_meses) || 0;

                  const contenidosLocal = convertirAMonedaLocal(
                    contenidosUnit * pantallas,
                    monedaContenidos,
                    pais,
                    fechaConversion
                  );
                  const montoMensualOriginal = contenidosUnit * pantallas;
                  const montoTotalOriginal = montoMensualOriginal * plazoCont;
                  const montoTotalCLP = contenidosLocal * plazoCont;

                  todas.push({
                    ...n,
                    id: `${n.id}-contenidos`,
                    esBTL: false,
                    esCarteleria: true,
                    esHardware: false,
                    cliente: `${nombreReferencia} (contenidos)`,
                    importe: montoTotalCLP,
                    importeOriginal: montoTotalOriginal,
                    monedaOriginal: monedaContenidos,
                    esMonedaUF: monedaContenidos.includes("UF"),
                    pantallas,
                    plazo: plazoCont,
                    feeGestion: 0,
                    feeContenidos: contenidosUnit,
                  });
                }
              }
            }
          });
        } else {
          todas = [
            ...todas,
            ...negociosCarteleria
              .filter((n) => {
                if (n.eliminada) return false;
                if (!["100%", "Activo"].includes(n.estado) || !n.fecha_inicio) return false;
                const fechaInicio = new Date(n.fecha_inicio);
                const plazoMeses = parseInt(n.plazo_contrato_meses) || 0;
                if (plazoMeses === 0) return false;
                const fechaFin = new Date(fechaInicio);
                fechaFin.setMonth(fechaFin.getMonth() + plazoMeses);
                const ultimoDiaPeriodo = new Date(ano, end, 0);
                const primerDiaPeriodo = new Date(ano, start - 1, 1);
                return fechaInicio <= ultimoDiaPeriodo && primerDiaPeriodo < fechaFin;
              })
              .map((n) => {
                const pantallas = parseInt(n.cantidad_pantallas) || 1;
                const plazo = parseInt(n.plazo_contrato_meses) || 0;
                const gestion = parseFloat(n.fee_gestion_unitario) || 0;
                const contenidos = n.incluye_contenidos ? parseFloat(n.fee_contenidos_unitario) || 0 : 0;

                const monedaGestion = (n.moneda_gestion || "UF").toUpperCase();
                const monedaContenidos = (n.moneda_contenidos || "UF").toUpperCase();
                const esUF = monedaGestion.includes("UF") || monedaContenidos.includes("UF");

                const montoMensualOriginal = (gestion + contenidos) * pantallas;

                const ultimoDiaMes = new Date(ano, end, 0);
                const fechaConversion = ultimoDiaMes.toISOString().split("T")[0];

                const gestionCLP = convertirAMonedaLocal(gestion * pantallas, monedaGestion, pais, fechaConversion);
                const contenidosCLP = convertirAMonedaLocal(
                  contenidos * pantallas,
                  monedaContenidos,
                  pais,
                  fechaConversion
                );
                const montoMensualCLP = gestionCLP + contenidosCLP;

                return {
                  ...n,
                  esBTL: false,
                  esCarteleria: true,
                  esHardware: false,
                  cliente: resolverNombreReferencia(n),
                  importe: montoMensualCLP,
                  importeOriginal: montoMensualOriginal,
                  monedaOriginal: monedaGestion,
                  esMonedaUF: esUF,
                  pantallas,
                  plazo,
                  feeGestion: gestion,
                  feeContenidos: contenidos,
                };
              }),
          ];
        }
      }

      const isFacturacion = tipo === "facturacion";
      setModal({
        open: true,
        title:
          `${isFacturacion ? "Facturación" : "Cierres"}` +
          `${vertical ? ` - ${vertical.charAt(0).toUpperCase() + vertical.slice(1)}` : ""}` +
          ` - ${getMonthName(monthIndex)} ${ano}`,
        items: todas.sort((a, b) => (b.importe || b.total_investment || 0) - (a.importe || a.total_investment || 0)),
      });
    },
    [
      oportunidades,
      salesBTL,
      negociosCarteleria,
      contratosData,
      usuarios,
      clientes,
      edificios,
      pais,
      currentYear,
      getMonthName,
    ]
  );

  const handleOpenPipelineModal = useCallback(
    (monthIndex, isTotal = false) => {
      const items = getPipelineItems(monthIndex, isTotal);
      const mesNombre = isTotal ? "TOTAL 2026" : getMonthName(monthIndex);

      let total = 0;
      items.forEach((op) => {
        const importe = parseFloat(op.importe) || 0;
        const factor = op.estatus === "90%" ? FACTOR_90 : FACTOR_75;
        const importeAjustado = importe * factor;
        if (showInDollars || modoCombinado) {
          const rateOp = op.paisOrigen === pais ? tipoCambioActual : tipoCambioOtroPais || tipoCambioActual;
          total += importeAjustado / rateOp;
        } else {
          total += importeAjustado;
        }
      });

      setPipelineModal({
        open: true,
        title: `Pipeline - ${mesNombre}`,
        items,
        total,
      });
    },
    [getPipelineItems, getMonthName, showInDollars, modoCombinado, tipoCambioActual, tipoCambioOtroPais, pais]
  );

  // === RENDER ===

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ventas-proyeccion-wrapper">
      <VPHeader
        vistaCompleta={vistaCompleta}
        setVistaCompleta={setVistaCompleta}
        mostrar2025={mostrar2025}
        setMostrar2025={setMostrar2025}
        showInDollars={showInDollars}
        setShowInDollars={setShowInDollars}
        verChileYPeru={verChileYPeru}
        setVerChileYPeru={setVerChileYPeru}
        verYTD={verYTD}
        setVerYTD={setVerYTD}
        pais={pais}
        logo={verChileYPeru ? regionLogo : undefined}
        getDesglose={getDesglose}
        getMoneda={getMoneda}
        getDisplayValue={getDisplayValue}
        getMonthName={getMonthName}
        currentMonthIndex={currentMonthIndex}
        calcularPipeline={calcularPipeline}
        calcularPipelineFacturacion={calcularPipelineFacturacion}
        onOpenChart={() => setChartModalOpen(true)}
      />

      <div className="ventas-proyeccion">
        <div className={styles.tableWrapper}>
          <VentasTable
            vistaCompleta={vistaCompleta}
            mostrar2025={mostrar2025}
            verYTD={verYTD}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            getMonthName={getMonthName}
            calcularVariacion={calcularVariacion}
            shouldShowVsAA={shouldShowVsAA}
            calcularPipelineBruto={calcularPipelineBruto}
            calcularPipelineBrutoTotal={calcularPipelineBrutoTotal}
            onOpenModal={handleOpenModal}
            onOpenPipelineModal={handleOpenPipelineModal}
            currentMonthIndex={currentMonthIndex}
            styles={styles}
          />

          <DetailsModal
            modal={modal}
            onClose={() => setModal({ open: false, title: "", items: [] })}
            showInDollars={showInDollars}
            getMoneda={getMoneda}
            localString={localString}
            pais={pais}
            tipoCambioActual={tipoCambioActual}
            styles={styles}
          />

          <PipelineModal
            pipelineModal={pipelineModal}
            onClose={() => setPipelineModal({ open: false, title: "", items: [], total: 0 })}
            pipelineSort={pipelineSort}
            setPipelineSort={setPipelineSort}
            showInDollars={showInDollars}
            modoCombinado={modoCombinado}
            clientes={clientes}
            moneda={moneda}
            localString={localString}
            tipoCambioActual={tipoCambioActual}
            pais={pais}
            styles={styles}
          />

          <ChartModal
            open={chartModalOpen}
            onClose={() => setChartModalOpen(false)}
            getDesglose={getDesglose}
            getMoneda={getMoneda}
            getDisplayValue={getDisplayValue}
            currentMonthIndex={currentMonthIndex}
            pais={pais}
            styles={styles}
            oportunidades={oportunidades}
            contratosData={contratosData}
            showInDollars={showInDollars}
          />
        </div>
      </div>
    </div>
  );
};

export default VentasProyeccion;
